import axios, { AxiosInstance, AxiosRequestConfig } from "@didi/dajax";
import { i18nTranslate } from "@didi/i18n-plugin";
import {
  BASE_PATH,
  DefaultConfigs,
  inApp,
  MX_CITY_ID,
  defaultLanguage,
} from "@/common/js/constant";
import { trackEvent } from "@/utils/omega";
import { Modal } from "antd-mobile";
import { getWsgenv } from "./wsgenv";
import { getWsgenvByBridge } from "./wsgenv-bridge";
import { reportPerformanceApi } from "./performance";
import { getSign } from "@didi/wsgsig";
import { checkLogin } from "@/views/MGM/common/bridge";

// 通用错误提示
export const ERROR_MESSAGE = {
  404: "Invalid API.",
  13100: "NOT LOGGED IN",
  NetworkBusy: "Network Busy.",
  RiskVerifyFailed: "Risk verify failed.",
};

// 错误类型
export enum ERROR_TYPE {
  // 成功
  Success = 0,
  SuccessNew = 200,
  // 没有登陆
  NotLogged = 101,
  // 风控核验
  RiskVerify = 500000,
  // 业务核验
  BusinessVerify = 500002,
  // 账号冻结
  FreezeAccount = 300000,
}
// 端内/外
enum ClientStatus {
  inApp = "0", // 端内
  webApp = "1", // 端外
}

const baseUrl = `${DefaultConfigs.publicAPIPath}`;

const defaultSettings = {
  // `baseURL` 将自动加在 `url` 前面，除非 `url` 是一个绝对 URL。
  baseURL: "",
  // 请求头
  headers: {
    "Content-Type": "application/json; charset=utf-8",
  },
  // `transformResponse` 在传递给 then/catch 前，允许修改响应数据
  transformResponse: [
    function (data: unknown) {
      // 对接收的 data 进行任意转换处理
      return data;
    },
  ],
  // 跨域请求时是否需要使用凭证(cookies)
  withCredentials: false,
  // 加签
  needEncrypt: false,
  // trace id
  withTraceId: true,
  // 请求超时时间
  timeout: 1000 * 60, // 60s
};

// 公参存储KEY
const KEY_FOR_CommonParams: string = "KFCP";

// 通用请求头
const CommonHeaders = new Map();

// 端内返回上一页面
const bridge = window.Fusion && window.Fusion.loadModule("SofiBridgeModule");

// 设置请求头
export function setHeader(key: string, value: string | number) {
  CommonHeaders.set(key, value);
}
// 公共参数
export const CommonParams: { [name: string]: any } = {};

// 从session中恢复公参
try {
  const cacheContent: string | null =
    window.sessionStorage.getItem(KEY_FOR_CommonParams);
  if (cacheContent) {
    Object.assign(CommonParams, JSON.parse(cacheContent));
  }
} catch (e) {
  console.log("获取session公参失败");
  // 技术打点
  trackEvent("tech_sofi_catch_error", {
    source: "utils.request.CommonParams",
    data: JSON.stringify(e),
  });
}

// 设置公参
export function setParam(key: string, value: string | number) {
  try {
    CommonParams[key] = value;
    // 利用session备份公参
    window.sessionStorage.setItem(
      KEY_FOR_CommonParams,
      JSON.stringify(CommonParams),
    );
  } catch (e) {
    console.log(e);
    // 技术打点
    trackEvent("tech_sofi_catch_error", {
      source: "utils.request.setParam",
      data: JSON.stringify(e),
    });
  }
}

const request: AxiosInstance = axios.create(defaultSettings);

type RequestTrackConfig = AxiosRequestConfig & {
  __requestStartTime?: number;
};

function getNow() {
  if (typeof window !== "undefined" && window.performance?.now) {
    return window.performance.now();
  }
  return Date.now();
}

function reportApiDuration(config?: RequestTrackConfig) {
  const startTime = config?.__requestStartTime;
  if (typeof startTime !== "number") {
    return;
  }

  const api = config?.url ? config.url.split("?")[0] : "unknown_api";
  const duration = Math.max(Math.round(getNow() - startTime), 0);
  // 异步上报且兜底异常，确保不影响主请求链路与页面渲染
  setTimeout(() => {
    try {
      reportPerformanceApi(api, duration);
    } catch (error) {
      trackEvent("tech_sofi_catch_error", {
        source: "utils.request.reportPerformanceApi",
        data: JSON.stringify(error),
      });
    }
  }, 0);
}

// hack @杨成 AmorcyYang
// fixed 升级@didi/dajax@3.0.7 @杨成 AmorcyYang
// axios.defaults.withTraceId = defaultSettings.withTraceId;

// 添加请求拦截器
request.interceptors.request.use(
  async function (config) {
    (config as RequestTrackConfig).__requestStartTime = getNow();
    // 技术打点 - 接口请求总次数
    trackEvent("tech_sofi_catch_error", {
      source: "utils.request.start",
      data: config.url,
    });
    // 在发送请求之前做些什么
    // 配置请求头
    config.headers = config.headers || {};
    // 判断是否为风控接口（sofi-risk-api），需要添加 location_cityid
    const isRiskApi = config.url && config.url.includes("sofi-risk-api");
    for (let [key, value] of CommonHeaders) {
      // 对 didi-header-hint-content 进行合并处理
      if (key === "didi-header-hint-content") {
        try {
          const commonValue = JSON.parse(value as string);
          // 风控接口添加 location_cityid
          if (isRiskApi) {
            config.headers[key] = JSON.stringify({
              ...commonValue,
              location_cityid: String(MX_CITY_ID),
            });
          } else {
            config.headers[key] = value;
          }
        } catch {
          config.headers[key] = value;
        }
      } else {
        config.headers[key] = value;
      }
    }
    // 配置请求公共参数
    config.params = config.params || {};
    for (let [key, value] of Object.entries(CommonParams)) {
      if (value !== undefined) {
        config.params[key] = value;
      }
    }

    // hack 端内每次请求获取token，解决token失效问题
    if (inApp) {
      try {
        const isLoggedIn = await checkLogin();
        if (isLoggedIn) {
          config.params["token"] = await new Promise((resolve) => {
            Fusion.loadModule("SofiBridgeModule").sfGetSofipoTicket(resolve);
          });
        }
      } catch (e) {
        console.log("err", e);
      }
    }
    // 集成集团安全SDK
    try {
      if (config.url) {
        // 把链接上的参数统一放到config.params里
        if (config.url.indexOf("?") > 0) {
          // 获取查询参数
          const params = new URLSearchParams(
            config.url.substring(config.url.indexOf("?")),
          );
          // 将URL中的查询参数添加到mergedParams
          params.forEach((value, key) => {
            config.params[key] = value;
          });
          // 删除链接上的参数
          config.url =
            config.url.substring(0, config.url.indexOf("?")) || config.url;
        }
        // 增加wsgenv参数
        // PS: wsgenv不要取data 获取到是什么就透传什么！！！！ https://git.xiaojukeji.com/BusinessAccessSecurity/frontend/Web/wsgsdk/blob/master/README.md
        // 限额页接口wsgenv获取方式改为bridge,其他接口保持sdk获取不变
        const useBridgeApi = config.url?.includes('/sofipo/v0/limit/updateLimit') || config.url?.includes('/sofipo/v0/limit/limitPage')
        if (useBridgeApi) {
          config.headers["wsgenv"] = await getWsgenvByBridge(config.url)
        } else {
          config.headers["wsgenv"] = await getWsgenv(config.url);
        }
      }
    } catch (e: unknown) {
      // 技术打点
      trackEvent("tech_sofi_catch_error", {
        source: "utils.request.wsgenv",
        data: JSON.stringify(e),
      });
    }
    // 如果token为空，删除该字段，避免计算签名错误
    if (
      config.params["token"] === undefined ||
      config.params["token"] === null
    ) {
      delete config.params["token"];
    }

    // 加签
    let funObj: {
      params: { [name: string]: any };
      contentType: string | number | boolean;
      signUpgrade: boolean;
      body?: any;
    } = {
      params: { ...config.params }, //如果没有请求参数，则不用传params参数
      contentType: config["headers"]["Content-Type"], //如果没有请求体，则也没有请求体的数据类型，故contentType参数也不用传
      signUpgrade: true, // 涉及计算签名耗时相关，见下一章节性能章节，(boolean类型：true或false，建议此参数必传，不要使用默认值)
    };

    //如果没有请求体，则不用传body参数
    if (config.data) {
      funObj.body = config.data;
    }

    // 生成签名
    let signature = getSign(funObj);
    if (signature) {
      config.params["wsgsig"] = signature; // 将签名拼接到接口请求参数中
    }

    return config;
  },
  function (error) {
    // 技术打点 - 接口请求异常次数
    trackEvent("tech_sofi_catch_error", {
      source: "utils.request.error",
      data: JSON.stringify(error),
    });
    // 对请求错误做些什么
    // return Promise.reject(error);
    return Promise.reject(
      { message: i18nTranslate("Fintech_Payment_KYC_Oilm_lUbJ") } || error,
    );
  },
);

// 添加响应拦截器
request.interceptors.response.use(
  function (response) {
    reportApiDuration(response?.config as RequestTrackConfig);
    // 请求成功
    trackEvent("tech_sofi_catch_error", {
      source: "utils.response.success",
      data: response?.config?.url,
    });
    // 2xx 范围内的状态码都会触发该函数。
    // 对响应数据做点什么
    // console.log(response)
    // return response;
    if (typeof response.data === "string") {
      return JSON.parse(response.data);
    } else {
      return response.data;
    }
  },
  function (error) {
    reportApiDuration(error?.config as RequestTrackConfig);
    // 请求失败
    trackEvent("tech_sofi_catch_error", {
      source: "utils.response.fail",
      data: JSON.stringify(error),
    });
    // 超出 2xx 范围的状态码都会触发该函数。
    // 对响应错误做点什么
    // return Promise.reject(error);
    return Promise.reject(
      {
        message: i18nTranslate("Fintech_Payment_KYC_Oilm_lUbJ"),
        errmsg:
          (error?.response?.data &&
            JSON.parse(error?.response?.data)?.errmsg) ||
          "",
        errno: error?.response?.status || "",
      } || error,
    );
  },
);

function handleResponse<T>(
  res: Promise<{
    errno: number;
    errNo?: number;
    data: any;
    i18nMsg: string;
    errmsg: string;
    errMsg: string;
  }>,
  raw: boolean = false,
): Promise<T> {
  return res.then(
    (ret) => {
      // 兼容风控驼峰字段写法
      ret.errno = ret.errno || ret.errNo || 0;
      ret.errmsg = ret.errmsg || ret.errMsg;
      // 请求成功
      if (
        ret.errno === ERROR_TYPE.Success ||
        ret.errno === ERROR_TYPE.SuccessNew
      ) {
        return Promise.resolve(ret.data || {});
      }
      // 登录失效
      if (ret.errno === ERROR_TYPE.NotLogged) {
        if (inApp) {
          bridge && bridge?.sfCloseNativePage();
        } else {
          // 返回不履约的Promise
          return new Promise(() => {
            // 登陆态失效 弹窗提示
            Modal.alert({
              className: "sofi-alert",
              // title: i18nTranslate('Fintech_Payment_AF_VBip_Mdss'),
              content: ret.errmsg || "",
              confirmText: i18nTranslate("Fintech_Payment_AF_iknn_PlqT"),
              onConfirm() {
                window.location.href = `${BASE_PATH}/welcome`;
              },
            });
          });
        }
        return Promise.reject(ret);
      }
      // 账号冻结
      if (ret.errno === ERROR_TYPE.FreezeAccount) {
        // 返回不履约的Promise
        return new Promise(() => {
          // 跳转冻结详情
          if (ret.data?.interceptionDomain?.jumpLink) {
            window.location.href = ret.data?.interceptionDomain?.jumpLink;
            return;
          }
          // 埋点上报 - 冻结弹窗展现
          trackEvent("fin_sofi_freeze_popup_sw", {
            source: ret.data?.source, // 出金下单冻结弹窗
            freeze_id: ret.data?.interceptionDomain?.freezeId, // 冻结id+文案二选一
            popup_message: ret.data?.interceptionDomain?.popUp?.subTitle,
            client_status: inApp ? ClientStatus.inApp : ClientStatus.webApp, // 客户端类型
          });
          // 弹窗冻结信息
          Modal.confirm({
            className: "sofi-alert",
            title:
              ret.data?.interceptionDomain?.popUp?.title || "Freeze Account",
            content:
              ret.data?.interceptionDomain?.popUp?.subTitle || "Freeze Account",
            confirmText:
              ret.data?.interceptionDomain?.popUp?.firstButton?.text ||
              "Confrim",
            cancelText:
              ret.data?.interceptionDomain?.popUp?.sencondButton?.text ||
              "Go Back",
            onConfirm() {
              // 埋点上报 - 点击view details
              trackEvent("fin_sofi_freeze_popup_ck", {
                source: ret.data?.source, // 出金下单冻结弹窗
                freeze_id: ret.data?.interceptionDomain?.freezeId, // 冻结id+文案二选一
                button_name: "view_details",
                popup_message: ret.data?.interceptionDomain?.popUp?.subTitle,
                client_status: inApp ? ClientStatus.inApp : ClientStatus.webApp, // 客户端类型
              });
              window.location.href =
                ret.data?.interceptionDomain?.popUp?.firstButton?.target ||
                `${BASE_PATH}/`;
            },
            onCancel() {
              // 埋点上报 - 点击go back
              trackEvent("fin_sofi_freeze_popup_ck", {
                source: ret.data?.source, // 出金下单冻结弹窗
                freeze_id: ret.data?.interceptionDomain?.freezeId, // 冻结id
                button_name: "go_back",
                popup_message: ret.data?.interceptionDomain?.popUp?.subTitle,
                client_status: inApp ? ClientStatus.inApp : ClientStatus.webApp, // 客户端类型
              });
              window.location.href = `${BASE_PATH}/`;
            },
          });
        });
      }
      // 原始数据
      if (raw) {
        return ret;
      }
      // friendly notify
      const notifys: string =
        ret.i18nMsg || ret.errmsg || ERROR_MESSAGE.NetworkBusy;
      return Promise.reject({
        message: notifys,
        errno: ret?.errno || "",
        data: ret.data,
      });
    },
    (error) => {
      return Promise.reject(error);
    },
  );
}

type RequestParams = {
  [key: string]: any;
};

type RequestConfig = AxiosRequestConfig & { raw?: boolean };

/**
 * Post
 * @author lawrencehou
 * @template T
 * @param url
 * @param params
 * @param opts
 * @returns t
 */
export function post<T>(
  url: string,
  params: RequestParams,
  opts?: RequestConfig,
): Promise<T> {
  return handleResponse<T>(request.post(url, params, opts), opts && opts.raw);
}

/**
 * Get
 * @author lawrencehou
 * @template T
 * @param url
 * @param opts
 * @returns t
 */
export function get<T>(url: string, opts?: RequestConfig): Promise<T> {
  return handleResponse<T>(request.get(url, opts), opts && opts.raw);
}

/**
 * Delete
 * @author lawrencehou
 * @template T
 * @param url
 * @param opts
 * @returns t
 */
export function del<T>(url: string, opts?: RequestConfig): Promise<T> {
  return handleResponse<T>(request.delete(url, opts), opts && opts.raw);
}

/**
 * Put
 * @author lawrencehou
 * @template T
 * @param url
 * @param params
 * @param opts
 * @returns t
 */
export function put<T>(
  url: string,
  params: RequestParams,
  opts?: RequestConfig,
): Promise<T> {
  return handleResponse<T>(request.put(url, params, opts), opts && opts.raw);
}
